import { CardContainer } from "@src/components/cards";
import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";
import { AppUtils, useWindowDimensions } from "@src/utils";
import { dateShortFormatterUTC } from "@src/constants/convertions";
import AdminHooks from "@src/context/hooks/adminHooks";
import {
  Button,
  Checkbox,
  DropDown,
  FormInput,
  Icon,
  Loader,
  SearchInput,
} from "@src/components/atoms";

const getFullName = (user) => {
  const name = (user.operatorName || "").trim();
  const lastName = (user.operatorLastName || "").trim();
  if (!lastName) return name;
  //Sometimes operatorName contains last name already - match whole trailing words only
  const lowerName = name.toLowerCase();
  const lowerLastName = lastName.toLowerCase();
  const nameHasLastName =
    lowerName === lowerLastName || lowerName.endsWith(` ${lowerLastName}`);
  return nameHasLastName ? name : `${name} ${lastName}`.trim();
};

const getColumns = (users, width, onEdit, handleDeactivate) => {
  let columns = [
    {
      header: "Active",
      accessorKey: "active",
      cell: ({ row }) => (
        <>
          <div className="flex flex-row items-center justify-around">
            <Checkbox
              checked={row.original.active}
              label={
                <>
                  {row.original.appUserId != null && (
                    <span title="signed up/registered for the app">
                      <Icon name="profile" inline />
                    </span>
                  )}
                </>
              }
              onChange={() => handleDeactivate(row.original.operatorCode)}
            />
          </div>
        </>
      ),
    },
    {
      header: "Code",
      accessorKey: "operatorCode",
      cell: ({ row }) => (
        <span className="font-mono text-md truncate">
          {row.original.operatorCode}
        </span>
      ),
    },
    {
      header: "Name",
      accessorKey: "operatorName",
      cell: ({ row }) => (
        <span className="truncate">{getFullName(row.original)}</span>
      ),
    },
    {
      header: "Email",
      accessorKey: "operatorEmail",
      cell: ({ row }) => {
        return (
          <span className="truncate">
            {row.original.operatorEmail || row.original.vehicleEmail}
          </span>
        );
      },
    },
    {
      header: "Latest Event",
      accessorKey: "lastEventDate",
      cell: ({ row }) =>
        row.original.lastEventDate
          ? moment(row.original.lastEventDate).format("ll HH:mm")
          : "",
    },
    {
      header: "Last Received Data",
      accessorKey: "lastReceivedData",
      cell: ({ row }) =>
        row.original.lastReceivedData
          ? dateShortFormatterUTC(row.original.lastReceivedData)
          : "",
    },
    {
      header: "Enrolled",
      accessorKey: "enrolledDate",
      cell: ({ row }) =>
        row.original.enrolledDate
          ? dateShortFormatterUTC(row.original.enrolledDate)
          : "",
    },
    {
      header: "Actions",
      cell: ({ row }) => (
        <div className="flex">
          <Button
            block={false}
            padding={false}
            className="my-1 py-0"
            onClick={() => onEdit(row.original)}
          >
            View
          </Button>
        </div>
      ),
    },
  ];

  return columns;
};

const UsersTable = ({ users, handleDeactivate, onEdit }) => {
  const { width } = useWindowDimensions();
  const [columnVisibility, setColumnVisibility] = useState({});
  const [sorting, setSorting] = useState([]);
  const [filter, setFilter] = useState("");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const filteredData = useMemo(() => {
    if (!filter) return users;
    const search = filter.toLowerCase();
    return users.filter((u) => {
      const fullName = getFullName(u).toLowerCase();
      const email = String(
        u.operatorEmail || u.vehicleEmail || "",
      ).toLowerCase();
      return (
        String(u.operatorCode).toLowerCase().includes(search) ||
        fullName.includes(search) ||
        (email.includes(search) ?? false)
      );
    });
  }, [users, filter]);

  const table = useReactTable({
    data: filteredData,
    columns: getColumns(users, width, onEdit, handleDeactivate),
    state: { columnVisibility, sorting, pagination },
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableSortingRemoval: false,
  });

  const renderTable = (table) =>
    !!table ? (
      <div className="p-2">
        <table className="w-full divide-y divide-gray-200 text-sm">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="border-b border-gray-500">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    className={
                      header.column.getCanSort()
                        ? "cursor-pointer select-none text-start"
                        : "text-start"
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className="border-b border-gray-200 hover:bg-primary-100"
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            {table.getFooterGroups().map((footerGroup) => (
              <tr key={footerGroup.id}>
                {footerGroup.headers.map((header) => (
                  <th key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.footer,
                          header.getContext(),
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </tfoot>
        </table>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-2 grow">
            <Button
              block={false}
              padding={false}
              innerPadding={false}
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              Previous
            </Button>
            <Button
              block={false}
              padding={false}
              innerPadding={false}
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Next
            </Button>

            <span className="grow">
              Page{" "}
              <input
                type="number"
                min={1}
                max={table.getPageCount()}
                value={table.getState().pagination.pageIndex + 1}
                onChange={(e) => {
                  const page = e.target.value ? Number(e.target.value) - 1 : 0;
                  table.setPageIndex(page);
                }}
                className="w-12 border rounded px-1 text-center"
              />{" "}
              of {table.getPageCount()}
            </span>
          </div>

          {/* Page size selector on far right */}
          <select
            value={table.getState().pagination.pageSize}
            onChange={(e) => table.setPageSize(Number(e.target.value))}
            className="border rounded px-2 py-1"
          >
            {[10, 25, 50, 1000].map((size) => (
              <option key={size} value={size}>
                Show {size}
              </option>
            ))}
          </select>
        </div>
      </div>
    ) : (
      <div>Error rendering the table...</div>
    );

  return (
    <>
      <SearchInput value={filter} onChange={(e) => setFilter(e.target.value)} />
      {renderTable(table)}
    </>
  );
};

export default UsersTable;
